import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const TeamSection = styled.section`
  padding: 8rem 2rem;
  max-width: 1100px;
  margin: 0 auto;
`;

const Title = styled(motion.h2)`
  font-size: 3.2rem;
  font-weight: 900;
  background: linear-gradient(135deg, #ffffffff, #3c3c3cff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  margin-bottom: 4rem;
  letter-spacing: -0.5px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
`;

const Card = styled(motion.div)`
  text-align: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 245, 255, 0.12);
  border-radius: 32px;
  padding: 2.5rem 2rem;
  backdrop-filter: blur(14px);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-12px);
    border-color: #00f5ff;
    box-shadow: 0 30px 60px rgba(0, 245, 255, 0.12);
  }
`;

const Photo = styled.div`
  width: 140px;
  height: 140px;
  margin: 0 auto 1.8rem;
  border-radius: 50%;
  background-image: url('${props => props.src}');
  background-size: cover;
  background-position: center;
  box-shadow: 0 8px 32px rgba(0, 245, 255, 0.15);
  transition: box-shadow 0.4s ease;

  ${Card}:hover & {
    box-shadow: 0 12px 40px rgba(0, 245, 255, 0.25);
  }

  ${props => !props.src && `
    background: #1a1a1a;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #555;
    font-size: 0.9rem;
  `}
`;

const Name = styled.h3`
  font-size: 1.5rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 0.5rem;
  letter-spacing: -0.3px;
`;

const Role = styled.p`
  font-size: 1.05rem;
  color: #d7d7d7ff;
  margin: 0 0 1.5rem;
  font-weight: 300;
  letter-spacing: 0.5px;
`;

const Socials = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const SocialLink = styled(motion.a)`
  width: 40px;
  height: 40px;
  background: rgba(0, 245, 255, 0.08);
  border: 1px solid rgba(0, 245, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00f5ff;
  transition: all 0.3s ease;

  &:hover {
    background: #00f5ff;
    color: #0a0a0a;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 245, 255, 0.3);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Team = () => {
  const { t } = useTranslation();

  const members = [
    {
      name: 'Dias Oralbekov',
      role: 'CEO & Founder',
      photo: '/images/dias.png',
      linkedin: 'https://www.linkedin.com/in/dias-oralbekov/',
      x: 'https://www.instagram.com/http.diass/'
    },
    {
      name: 'Sapargali Bekzat',
      role: 'CMO & Co-Founder',
      photo: '/images/bekzat.png',
      linkedin: 'https://www.linkedin.com/in/bekzat-sapargali-878240260/',
      x: 'https://x.com/bekzat'
    },
    {
      name: 'Askar Ali',
      role: 'CFO & Co-Founder',
      photo: '/images/askar.jpeg',
      linkedin: 'https://www.linkedin.com/in/aali0108/',
      x: 'https://www.instagram.com/aalikz_/'
    },
    {
      name: 'Adilkhan Kusmanov',
      role: 'Team Lead of Plano AI',
      photo: '/images/adilkhan.jpg',
      linkedin: 'https://www.linkedin.com/in/adilkhan-kusmanov-221b66398/',
      x: 'https://www.instagram.com/adilkhan.kusmanov/' // если есть
    }
  ];

  return (
    <TeamSection>
      <Title
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        {t('team_title')}
      </Title>

      <Grid>
        {members.map((m, i) => (
          <Card
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15, duration: 0.7 }}
          >
            <Photo src={m.photo} />
            <Name>{m.name}</Name>
            <Role>{m.role}</Role>
            <Socials>
              <SocialLink
                href={m.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.979 0 1.771-.773 1.771-1.729V1.729C24 .774 23.208 0 22.225 0z"/>
                </svg>
              </SocialLink>

              <SocialLink
                href={m.x}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </SocialLink>
            </Socials>
          </Card>
        ))}
      </Grid>
    </TeamSection>
  );
};

export default Team;